import React, { useEffect, useState, useMemo } from "react";
import { ShieldCheck, Copy, Trash2, Plus, Edit } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { FormSection, FormField, StickyActionBar } from "@/components/common/FormSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api, formatApiError } from "@/lib/api";

// Backend role contract (backendv2 RoleResource / Store|UpdateRoleRequest):
//   name         — machine code, lower snake_case, immutable, unique. hasRole() key.
//   display_name — human label (required).
//   description  — optional.
//   permissions  — flat `{module}.{action}` slugs, validated against the catalog.
//   is_system    — protected: name/is_system immutable, cannot be deleted.
// Envelope is always { data: ... }. There is NO `code` or `status` field.
const EMPTY = { name: "", display_name: "", description: "", permissions: [] };

export default function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [confirm, setConfirm] = useState({ open: false });

  async function loadRoles(keepId) {
    setLoading(true);
    try {
      const res = await api.get("/roles");
      const list = res.data.data || [];
      setRoles(list);
      const targetId = keepId ?? selected?.id;
      const next = list.find((r) => r.id === targetId) || list[0] || null;
      setSelected(next);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => {
    loadRoles();
    // Permission catalog: { data: { module: [actions] } }.
    api.get("/roles/permission-catalog").then((r) => setCatalog(r.data.data || {})).catch(() => {});
    // eslint-disable-next-line
  }, []);

  function openCreate() {
    setEditing(null); setForm(EMPTY); setErrors({}); setSheetOpen(true);
  }
  function openEdit(role) {
    setEditing(role); setErrors({});
    setForm({
      name: role.name,
      display_name: role.display_name || "",
      description: role.description || "",
      permissions: role.permissions || [],
    });
    setSheetOpen(true);
  }

  function validate() {
    const e = {};
    if (!editing) {
      if (!form.name) e.name = "Role code is required";
      else if (!/^[a-z][a-z0-9_]*$/.test(form.name)) e.name = "Lower snake_case only (letters, numbers, underscores)";
    }
    if (!form.display_name) e.display_name = "Display name is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function save() {
    if (!validate()) return;
    setSaving(true);
    try {
      if (editing) {
        const res = await api.put(`/roles/${editing.id}`, {
          display_name: form.display_name,
          description: form.description,
          permissions: form.permissions,
        });
        toast.success("Role updated");
        setSheetOpen(false);
        await loadRoles(res.data.data?.id);
      } else {
        const res = await api.post("/roles", {
          name: form.name,
          display_name: form.display_name,
          description: form.description,
          permissions: form.permissions,
        });
        toast.success("Role created");
        setSheetOpen(false);
        await loadRoles(res.data.data?.id);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  async function clone(role) {
    try {
      const res = await api.post(`/roles/${role.id}/clone`);
      toast.success("Role cloned");
      await loadRoles(res.data.data?.id);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function remove(role) {
    try {
      await api.delete(`/roles/${role.id}`);
      toast.success("Role deleted");
      await loadRoles(null);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function togglePermissionInline(role, perm, checked) {
    const next = checked
      ? Array.from(new Set([...(role.permissions || []), perm]))
      : (role.permissions || []).filter((p) => p !== perm);
    try {
      const res = await api.put(`/roles/${role.id}`, { permissions: next });
      const updated = res.data.data;
      setSelected(updated);
      setRoles((rs) => rs.map((r) => (r.id === role.id ? updated : r)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const totalPerms = useMemo(() => Object.values(catalog).reduce((a, arr) => a + arr.length, 0), [catalog]);

  return (
    <div data-testid="roles-page">
      <PageHeader
        title="Roles & Permissions"
        subtitle="Configure role-based access and clone permission templates."
        actions={
          <Button onClick={openCreate} data-testid="create-role-button" className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">
            <Plus className="w-4 h-4 mr-1.5" /> New Role
          </Button>
        }
      />

      <div className="grid grid-cols-12 gap-4">
        {/* Roles list */}
        <div className="col-span-12 lg:col-span-4 hah-card p-3" data-testid="roles-list">
          <div className="px-2 py-2 text-[11px] uppercase tracking-wider font-semibold text-[#6B7280] flex items-center justify-between">
            <span>Roles</span>
            <span>{roles.length}</span>
          </div>
          <div className="space-y-1 max-h-[560px] overflow-y-auto">
            {loading && <div className="px-3 py-4 text-sm text-[#6B7280]">Loading…</div>}
            {roles.map((r) => (
              <button
                key={r.id}
                data-testid={`role-item-${r.name}`}
                onClick={() => setSelected(r)}
                className={`w-full text-left px-3 py-2.5 rounded-lg flex items-start gap-3 transition-colors ${selected?.id === r.id ? "bg-[#EAF8FA]" : "hover:bg-[#F5F7FA]"}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${selected?.id === r.id ? "bg-[#0B7E8C] text-white" : "bg-[#F5F7FA] text-[#0B7E8C]"}`}>
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-[#1F2937] truncate">{r.display_name || r.name}</span>
                    {r.is_system && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#06B6D414] text-[#06B6D4]">System</span>}
                  </div>
                  <div className="text-xs text-[#6B7280] truncate">
                    {r.name} · {(r.permissions || []).length} permissions
                    {typeof r.users_count === "number" ? ` · ${r.users_count} users` : ""}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Permission matrix */}
        <div className="col-span-12 lg:col-span-8 hah-card p-5" data-testid="permission-matrix">
          {selected ? (
            <>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="hah-section-title">Permission matrix</div>
                  <h3 className="text-base font-semibold text-[#1F2937] mt-0.5">
                    {selected.display_name || selected.name}
                    <span className="ml-2 text-xs font-normal text-[#6B7280]">{selected.name}</span>
                  </h3>
                  <p className="text-sm text-[#6B7280] mt-1">{selected.description || "No description"}</p>
                  <div className="text-xs text-[#6B7280] mt-2">
                    {(selected.permissions || []).length} of {totalPerms} permissions granted
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => openEdit(selected)} className="border-[#E5E7EB]" data-testid="edit-role">
                    <Edit className="w-4 h-4 mr-1.5" /> Edit
                  </Button>
                  <Button variant="outline" onClick={() => clone(selected)} className="border-[#E5E7EB]" data-testid="clone-role">
                    <Copy className="w-4 h-4 mr-1.5" /> Clone
                  </Button>
                  {!selected.is_system && (
                    <Button
                      variant="outline"
                      className="border-[#EF444433] text-[#EF4444] hover:bg-[#EF444412]"
                      onClick={() => setConfirm({ open: true, title: "Delete role", description: `Delete role "${selected.display_name || selected.name}"?`, destructive: true, onConfirm: () => remove(selected) })}
                      data-testid="delete-role"
                    >
                      <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                    </Button>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                {Object.entries(catalog).map(([mod, actions]) => (
                  <div key={mod} className="border border-[#E5E7EB] rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="capitalize font-medium text-[#1F2937]">{mod}</div>
                      <div className="text-xs text-[#6B7280]">
                        {actions.filter((a) => (selected.permissions || []).includes(`${mod}.${a}`)).length} / {actions.length}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {actions.map((act) => {
                        const perm = `${mod}.${act}`;
                        const checked = (selected.permissions || []).includes(perm);
                        return (
                          <label key={perm} className="flex items-center gap-2 text-sm text-[#1F2937]">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(v) => togglePermissionInline(selected, perm, !!v)}
                              data-testid={`perm-${perm}`}
                            />
                            <span className="capitalize">{act.replace(/_/g, " ")}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {Object.keys(catalog).length === 0 && (
                  <div className="text-sm text-[#6B7280]">Permission catalog unavailable.</div>
                )}
              </div>
            </>
          ) : (
            <div className="text-sm text-[#6B7280] py-12 text-center">Select a role to view permissions.</div>
          )}
        </div>
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-[600px] overflow-y-auto p-0 bg-[#FAFBFC]">
          <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
            <SheetTitle>{editing ? "Edit role" : "Create role"}</SheetTitle>
            <SheetDescription>{editing ? `Code: ${editing.name}` : "Define a new role for the platform."}</SheetDescription>
          </SheetHeader>
          <div className="p-6 space-y-4">
            <FormSection title="General Information" testId="role-form-general">
              <FormField label="Role code" required error={errors.name}>
                <Input
                  data-testid="role-name"
                  disabled={!!editing}
                  placeholder="e.g. ward_pharmacist"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </FormField>
              <FormField label="Display name" required error={errors.display_name}>
                <Input
                  data-testid="role-display-name"
                  value={form.display_name}
                  onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                />
              </FormField>
              <FormField label="Description" full>
                <Textarea data-testid="role-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </FormField>
            </FormSection>
            {editing && (
              <p className="text-xs text-[#6B7280]">
                Edit permissions from the matrix after saving. The role code can't be changed once created.
              </p>
            )}
          </div>
          <StickyActionBar>
            <Button variant="outline" onClick={() => setSheetOpen(false)} className="border-[#E5E7EB]" data-testid="role-form-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="role-form-save" className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">
              {saving ? "Saving…" : editing ? "Save changes" : "Create role"}
            </Button>
          </StickyActionBar>
        </SheetContent>
      </Sheet>

      <ConfirmDialog open={confirm.open} onOpenChange={(o) => setConfirm({ ...confirm, open: o })}
        title={confirm.title} description={confirm.description} destructive={confirm.destructive}
        onConfirm={() => { confirm.onConfirm?.(); setConfirm({ open: false }); }} />
    </div>
  );
}
